import { Box, Stack, Typography } from '@mui/material';
import { Link } from 'react-router';
import { isAfter, parseISO } from 'date-fns';
import { iriToId } from '../../util';
import { formatTimeAgo } from '../../models/Chat';
import { StackedAvatars } from './';
import { Api } from '../../api';

type ActiveChatProps = {
  type: 'active';
  chat: Api.ChatConversation.V_Active;
};

type BroadcastChatProps = {
  type: 'broadcast';
  chat: Api.ChatConversation.V_Broadcast;
};

type ArchivedChatProps = {
  type: 'archived';
  chat: Api.ChatConversation.V_Archived;
};

type Props = ActiveChatProps | BroadcastChatProps | ArchivedChatProps;

const containerStyles = {
  px: 1.5,
  py: 2,
  backgroundColor: 'white',
  cursor: 'pointer',
  textDecoration: 'none',
  color: 'inherit',
  borderRadius: '20px',
  '&:hover': { backgroundColor: '#F6F7F7' },
};

function getSubtitle(conversation: any) {
  if (conversation?.lastMessage?.media?.length) {
    const mediaItem = conversation.lastMessage.media[0];
    if (mediaItem.mimeType?.startsWith('image/')) {
      return 'Sent a photo';
    }
    return 'Sent a file';
  }
  return conversation?.lastMessage?.message ?? 'No messages yet';
}

function getEntityInfo(
  chat: any,
  type: string,
): {
  id: string;
  entityType: 'user' | 'team';
  name: string;
  displayPictureIds: string[];
} {
  if (type === 'broadcast') {
    // Check if it's a team or user broadcast
    if (chat.team) {
      // For team broadcasts, collect display picture IDs from team members
      const memberPictureIds = (chat.teamMembers || [])
        .map((member: any) => member.displayPicture?.id)
        .filter(Boolean);

      return {
        id: iriToId(chat.team['@id'] || ''),
        entityType: 'team',
        name: `${chat.team.name || 'Unknown Team'}${chat.team.organisation ? ` (${chat.team.organisation.name})` : ''}`,
        displayPictureIds: memberPictureIds,
      };
    } else if (chat.user) {
      return {
        id: iriToId(chat.user['@id'] || ''),
        entityType: 'user',
        name: chat.user.name ?? 'Unknown User',
        displayPictureIds: chat.user.displayPicture
          ? [chat.user.displayPicture.id]
          : [],
      };
    }
  }

  // For active/archived, it's always a user
  return {
    id: iriToId(chat.user?.['@id'] || ''),
    entityType: 'user',
    name: chat.user?.name ?? 'Unknown User',
    displayPictureIds: chat.user?.displayPicture
      ? [chat.user.displayPicture.id]
      : [],
  };
}

function getLastMessageTime(chat: any): string | undefined {
  return chat.conversation?.lastMessage?.createdAt;
}

function hasUnreadMessage(_type: Props['type'], chat: Props['chat']): boolean {
  // Disabled because founders and physicians need to see unread indicators for broadcast chats.
  // if (type === 'broadcast') return false;

  const lastMessage = chat.conversation?.lastMessage;
  if (!lastMessage || typeof lastMessage !== 'object') return false;

  const lastReadAt = chat.conversation?.clinicianLastReadAt;
  if (!lastReadAt) return true;

  return isAfter(parseISO(lastMessage.createdAt), parseISO(lastReadAt));
}

export default function ChatListItem({ type, chat }: Props) {
  const entityInfo = getEntityInfo(chat, type);

  // Build URL based on entity type and chat type
  let to: string;
  if (type === 'broadcast') {
    const broadcastPath =
      entityInfo.entityType === 'team' ? 'broadcast/team' : 'broadcast/user';
    to = `/chat/${broadcastPath}/${entityInfo.id}`;
  } else {
    to = `/chat/${type}/${entityInfo.id}`;
  }

  const lastMessageTime = getLastMessageTime(chat);
  const subtitle = getSubtitle(chat.conversation);
  const showUnreadIndicator = hasUnreadMessage(type, chat);

  return (
    <Box component={Link} to={to} sx={containerStyles}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <StackedAvatars
          displayPictureIds={entityInfo.displayPictureIds}
          fallbackName={entityInfo.name}
          size="large"
        />
        <Stack flex={1} spacing={0}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ color: '#101828' }}
            >
              {entityInfo.name}
            </Typography>
            {lastMessageTime && (
              <Typography
                variant="caption"
                sx={{ color: '#667085', fontSize: '12px' }}
              >
                {formatTimeAgo(lastMessageTime)}
              </Typography>
            )}
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: 0.5 }}
          >
            {showUnreadIndicator && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#2D556C',
                  flexShrink: 0,
                }}
              />
            )}
            <Typography
              variant="body2"
              sx={{ color: '#667085', fontSize: '14px' }}
            >
              {subtitle}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
